import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useHistory } from "react-router-dom"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field"
import { TradeFilterSelectFullWidth } from "@/components/trade-filter-selects"
import { cn } from "@/lib/utils"
import { Info } from "lucide-react"
import {
  postDemoPrep,
  postDemoResolve,
  abandonDemoEntryAnalysis,
} from "@/services/demoEntryAnalysisApi"
import type { DemoCandleTimeframe, DemoListingObject } from "@/types/demoEntryAnalysis"
import { trackDemoEntryAnalysisEvent } from "@/utils/demoEntryAnalysisAnalytics"
import {
  DEMO_FUTURES_RECENT_TAIL_COPY,
  DEMO_NONE_LOCATABLE_COPY,
  DEMO_SYNC_OTHER_CSV_COPY,
  demoErrorUserMessage,
  isDemoEntryAnalysisInflight,
  isSyncOtherCsvCta,
} from "@/utils/demoEntryAnalysisErrors"
import { demoFuturesRecentTailBlockedMessage } from "@/utils/demoFuturesRecentTailGate"
import {
  DEMO_ENTRY_ANALYSIS_BASE_PATH,
  readDemoSession,
  restartDemoEntryAnalysis,
  writeDemoSession,
} from "@/utils/demoEntryAnalysisSession"
import {
  buildReportInstrumentFieldDescription,
  buildReportInstrumentSelectOptions,
  isInstrumentSelectableForContextReport,
} from "@/utils/reportInstrumentSelection"
import {
  defaultMarketDataSourceOption,
  formatMarketDataSourceSelectLabel,
  marketDataBrokerKey,
  needsMarketDataSourceChoiceForRow,
} from "@/utils/marketDataSourceChoice"
import {
  applyDemoCentralInstrumentRows,
  demoCandleTimeframeFieldDescription,
  demoCandleTimeframeOptions,
  isDemoCentralOhlcBroker,
  isDemoDatabentoFuturesListing,
  normalizeDemoCandleTimeframe,
  pickDemoMarketDataSourceForQuoteLane,
} from "@/utils/demoEntryAnalysisOhlc"
import {
  findQuoteVariantCandidate,
  isQuoteVariantOhlcSupported,
  normalizeQuoteVariantCandidates,
  qualifiedQuoteVariantForResolve,
  quoteVariantSelectOptions,
  resolveInitialQuoteVariant,
} from "@/utils/instrumentSetupSave"
import type { MarketDataSourceOption } from "@/types/marketDataSources"
import { DEFAULT_REPORT_CANDLE_TIMEFRAME } from "@/views/Dashboard/Reports/generateReportConstants"
import {
  AUTH_FONT,
  AUTH_MUTED_TEXT_CLASS,
  DemoEntryAnalysisShell,
  DemoLoadingBlock,
} from "./DemoEntryAnalysisShell"
import { DemoWizardFooter } from "./DemoWizardFooter"

const FIELD_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]"

const FIELD_DESC_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-muted-foreground"

const DEMO_ALERT_BTN_CLASS =
  "h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"

function DemoWizardRecoveryAlert(props: {
  title: string
  message: string
  showSyncAnother?: boolean
  onSyncAnother: () => void
  onStartOver: () => void
}): React.ReactElement {
  return (
    <Alert variant="info">
      <Info aria-hidden />
      <AlertTitle
        className={cn(
          AUTH_FONT,
          "text-sm font-medium leading-5 text-[color:var(--brand-tropical-surf)]"
        )}
      >
        {props.title}
      </AlertTitle>
      <AlertDescription
        className={cn(
          AUTH_FONT,
          "group-has-[>svg]/alert:col-start-2 space-y-3 text-sm font-normal leading-5 text-gray-500 dark:text-white"
        )}
      >
        <span className="block">{props.message}</span>
        <div className="flex flex-wrap items-center gap-2 pt-0.5">
          {props.showSyncAnother ? (
            <Button
              type="button"
              variant="outline-brand"
              size="sm"
              data-font="primary"
              className={DEMO_ALERT_BTN_CLASS}
              onClick={props.onSyncAnother}
            >
              Sync another CSV
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline-gray"
            size="sm"
            data-font="primary"
            className={DEMO_ALERT_BTN_CLASS}
            onClick={props.onStartOver}
          >
            Start over
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  )
}

/** Stable select value — broker slug alone is not unique (Binance Spot vs Futures). */
function demoMdSourceValue(source: MarketDataSourceOption, index: number): string {
  return String(source.md_listing_key || source.option_id || `idx:${index}`)
}

function defaultTf(): DemoCandleTimeframe {
  const tf = String(DEFAULT_REPORT_CANDLE_TIMEFRAME)
  return tf === "30m" ? "30m" : "1h"
}

function DemoWizardFieldBlock(props: {
  label: string
  description: React.ReactNode
  required?: boolean
  children: React.ReactNode
}): React.ReactElement {
  return (
    <Field
      className="w-full min-w-0 content-start [grid-template-rows:auto_minmax(2.25rem,auto)_auto]"
      data-demo-instrument-fields=""
    >
      <FieldLabel className={`${FIELD_LABEL_CLASS} min-h-5 leading-5`}>
        {props.label}
        {props.required ? (
          <span className="ml-0.5 text-[#E00101]" aria-hidden>
            *
          </span>
        ) : null}
      </FieldLabel>
      <div className="flex min-h-9 w-full min-w-0 flex-col items-stretch gap-1">
        {props.children}
      </div>
      <FieldDescription className={FIELD_DESC_CLASS}>{props.description}</FieldDescription>
    </Field>
  )
}

export default function DemoWizardPage(): React.ReactElement {
  const history = useHistory()
  const session = useMemo(() => readDemoSession(), [])
  const instruments = useMemo(
    () => applyDemoCentralInstrumentRows(session?.instruments || []),
    [session?.instruments]
  )

  const [symbol, setSymbol] = useState(
    () => session?.symbol || session?.suggestedSymbol || instruments[0]?.symbol || ""
  )
  const [candleTimeframe, setCandleTimeframe] = useState<DemoCandleTimeframe>(
    () => session?.candleTimeframe || defaultTf()
  )
  const [mdSourceKey, setMdSourceKey] = useState("")
  const [quoteVariant, setQuoteVariant] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Sync lock — React `busy` state alone cannot stop a double-click before re-render. */
  const prepInFlightRef = useRef(false)

  useEffect(() => {
    if (!session?.cohortId || !instruments.length) {
      history.replace(DEMO_ENTRY_ANALYSIS_BASE_PATH)
    }
  }, [history, instruments.length, session?.cohortId])

  const selectedRow = useMemo(
    () => instruments.find((r) => r.symbol === symbol) || null,
    [instruments, symbol]
  )

  const instrumentOptions = useMemo(
    () => buildReportInstrumentSelectOptions(instruments),
    [instruments]
  )

  const instrumentDescription = useMemo(
    () =>
      buildReportInstrumentFieldDescription({
        instruments,
        instrumentsLoading: false,
        symbolRequired: true,
      }),
    [instruments]
  )

  const quoteCandidates = useMemo(
    () => normalizeQuoteVariantCandidates(selectedRow?.quote_variant_candidates),
    [selectedRow]
  )

  const needsQuoteChoice = Boolean(
    selectedRow?.requires_quote_variant_choice && quoteCandidates.length > 0
  )

  const quoteOptions = useMemo(
    () => quoteVariantSelectOptions(quoteCandidates),
    [quoteCandidates]
  )

  // Changing instrument/TF/quote invalidates any prior prep job so we never reuse the wrong one.
  useEffect(() => {
    const current = readDemoSession()
    if (!current?.jobId) return
    const currentMd =
      current.listing?.market_data_symbol || current.listing?.resolved_symbol || ""
    const chosenMd = quoteVariant
      ? qualifiedQuoteVariantForResolve(quoteCandidates, quoteVariant)
      : ""
    if (
      current.symbol === symbol &&
      current.candleTimeframe === candleTimeframe &&
      (!chosenMd || currentMd === chosenMd || currentMd === quoteVariant)
    ) {
      return
    }
    writeDemoSession({
      jobId: undefined,
      handoffCode: undefined,
      pollToken: undefined,
      claimed: false,
      claimAttempted: false,
      readyPayload: null,
      step: "wizard",
    })
  }, [candleTimeframe, quoteCandidates, quoteVariant, symbol])

  useEffect(() => {
    if (!needsQuoteChoice) {
      setQuoteVariant("")
      return
    }
    setQuoteVariant(
      resolveInitialQuoteVariant(selectedRow?.suggested_quote_variant, quoteCandidates)
    )
  }, [needsQuoteChoice, quoteCandidates, selectedRow?.suggested_quote_variant])

  const mdSourceOptions = useMemo(() => {
    if (!selectedRow) return []
    return selectedRow.market_data_sources || []
  }, [selectedRow])

  const needsMdChoice = selectedRow ? needsMarketDataSourceChoiceForRow(selectedRow) : false

  const selectedMdSource = useMemo(() => {
    if (!mdSourceOptions.length) return null
    const byKey = mdSourceOptions.find(
      (src, index) => demoMdSourceValue(src, index) === mdSourceKey
    )
    if (byKey) return byKey
    return (
      defaultMarketDataSourceOption(mdSourceOptions) ||
      selectedRow?.selected_market_data_source ||
      mdSourceOptions[0] ||
      null
    )
  }, [mdSourceKey, mdSourceOptions, selectedRow])

  const databentoListing = useMemo(
    () =>
      isDemoDatabentoFuturesListing({
        source: selectedMdSource,
        marketDataBroker:
          selectedMdSource?.market_data_broker ||
          selectedMdSource?.broker ||
          selectedRow?.market_data_broker ||
          null,
      }),
    [selectedMdSource, selectedRow?.market_data_broker]
  )

  const candleTimeframeOptions = useMemo(
    () => demoCandleTimeframeOptions(databentoListing),
    [databentoListing]
  )

  const candleTimeframeDescription = useMemo(
    () => demoCandleTimeframeFieldDescription(databentoListing),
    [databentoListing]
  )

  useEffect(() => {
    setCandleTimeframe((current) => normalizeDemoCandleTimeframe(current, databentoListing))
  }, [databentoListing])

  useEffect(() => {
    if (!selectedRow || !mdSourceOptions.length) {
      setMdSourceKey("")
      return
    }

    // Align central OHLC listing with quote / perp lane when multiple sources exist.
    let preferred =
      selectedRow.selected_market_data_source ||
      defaultMarketDataSourceOption(mdSourceOptions) ||
      mdSourceOptions[0]

    if (needsQuoteChoice && quoteVariant) {
      const matched = pickDemoMarketDataSourceForQuoteLane(
        mdSourceOptions,
        quoteVariant,
        quoteCandidates
      )
      if (matched) preferred = matched
    }

    const idx = preferred
      ? Math.max(
          0,
          mdSourceOptions.findIndex(
            (s) =>
              (preferred.md_listing_key && s.md_listing_key === preferred.md_listing_key) ||
              (preferred.option_id && s.option_id === preferred.option_id) ||
              s === preferred
          )
        )
      : 0
    setMdSourceKey(demoMdSourceValue(mdSourceOptions[idx] || mdSourceOptions[0], idx))
  }, [mdSourceOptions, needsQuoteChoice, quoteCandidates, quoteVariant, selectedRow])

  const noneLocatable = useMemo(() => {
    if (!instruments.length) return true
    return !instruments.some((row) => isInstrumentSelectableForContextReport(row))
  }, [instruments])

  const futuresRecentTailBlocked = demoFuturesRecentTailBlockedMessage({
    brokerName: session?.brokerName,
    latestFillUtcMs: session?.latestFillUtcMs,
  })

  const handleInstrumentChange = useCallback(
    (value: string) => {
      const row = instruments.find((r) => r.symbol === value)
      if (row && !isInstrumentSelectableForContextReport(row)) return
      setSymbol(value)
      setError(null)
    },
    [instruments]
  )

  const handleQuoteVariantChange = useCallback((value: string) => {
    setQuoteVariant(value)
    setError(null)
  }, [])

  const onContinue = useCallback(async () => {
    if (prepInFlightRef.current || busy) return
    if (!session?.cohortId) {
      history.replace(DEMO_ENTRY_ANALYSIS_BASE_PATH)
      return
    }
    if (noneLocatable) {
      setError(DEMO_NONE_LOCATABLE_COPY)
      return
    }
    if (futuresRecentTailBlocked) {
      setError(futuresRecentTailBlocked)
      return
    }
    if (!symbol || !selectedRow) {
      setError("Choose an instrument to continue.")
      return
    }
    if (!isInstrumentSelectableForContextReport(selectedRow)) {
      setError(DEMO_SYNC_OTHER_CSV_COPY)
      return
    }

    const tradeSymbol = symbol
    // Use the picker select value as-is (candidate.symbol). Do NOT remap via display_symbol —
    // AAVEUSDT → spot resolve; AAVEUSDTPERP → futures resolve.
    const marketDataSymbol = needsQuoteChoice
      ? quoteVariant.trim() || undefined
      : selectedRow.market_data_symbol?.trim() || undefined

    if (needsQuoteChoice && !marketDataSymbol) {
      setError("Choose a quote variant for market data.")
      return
    }

    if (needsQuoteChoice) {
      const chosen = findQuoteVariantCandidate(quoteCandidates, quoteVariant)
      if (chosen && !isQuoteVariantOhlcSupported(chosen)) {
        setError(
          "That quote lane has no market data OHLC. Choose a USDT or USDC variant (or another OHLC-ready option)."
        )
        return
      }
    }

    // Same selection already prepped → resume generating without a second POST /prep.
    const existing = readDemoSession()
    const existingMdSymbol =
      existing?.listing?.market_data_symbol || existing?.listing?.resolved_symbol || null
    if (
      existing?.jobId &&
      existing.handoffCode &&
      existing.cohortId === session.cohortId &&
      existing.symbol === tradeSymbol &&
      existing.candleTimeframe === candleTimeframe &&
      (!needsQuoteChoice || existingMdSymbol === marketDataSymbol)
    ) {
      writeDemoSession({ step: "generating" })
      trackDemoEntryAnalysisEvent("prep_reused", { job_id: existing.jobId, symbol: tradeSymbol })
      history.push(`${DEMO_ENTRY_ANALYSIS_BASE_PATH}/generating`)
      return
    }

    prepInFlightRef.current = true
    setBusy(true)
    setError(null)
    trackDemoEntryAnalysisEvent("wizard_continue", {
      symbol: tradeSymbol,
      market_data_symbol: marketDataSymbol || "",
      candle_timeframe: candleTimeframe,
    })

    try {
      const mdSource =
        selectedMdSource ||
        selectedRow.selected_market_data_source ||
        null

      const centralMdSource =
        mdSource && isDemoCentralOhlcBroker(marketDataBrokerKey(mdSource)) ? mdSource : null

      // After a quote pick, resolve MUST use the picked symbol (AAVEUSDTPERP / AAVEUSDT),
      // not bare CSV AAVE — otherwise BE keeps returning picker-required / unresolved.
      const resolveBody = needsQuoteChoice
        ? {
            symbol: marketDataSymbol!,
            asset_class: selectedRow.asset_class ?? "crypto",
          }
        : {
            symbol: tradeSymbol,
            asset_class: selectedRow.asset_class ?? null,
            ...(centralMdSource
              ? {
                  market_data_broker:
                    centralMdSource.market_data_broker ||
                    centralMdSource.broker ||
                    null,
                  md_listing_key: centralMdSource.md_listing_key || null,
                  ibkr_conid: centralMdSource.ibkr_conid ?? null,
                  ibkr_exchange: centralMdSource.ibkr_exchange ?? null,
                }
              : {}),
            market_data_symbol: marketDataSymbol || null,
          }

      const resolved = await postDemoResolve(resolveBody)
      if (resolved.resolved === false || isSyncOtherCsvCta(resolved.cta)) {
        setError(resolved.message || DEMO_SYNC_OTHER_CSV_COPY)
        trackDemoEntryAnalysisEvent("instrument_fail", {
          symbol: tradeSymbol,
          resolve_symbol: resolveBody.symbol,
        })
        return
      }

      const listing: DemoListingObject = {
        symbol: tradeSymbol,
        asset_class:
          resolved.listing?.asset_class ?? resolveBody.asset_class ?? null,
        market_data_broker: resolved.listing?.market_data_broker ?? null,
        md_listing_key: resolved.listing?.md_listing_key ?? null,
        ibkr_conid: resolved.listing?.ibkr_conid ?? null,
        ibkr_exchange: resolved.listing?.ibkr_exchange ?? null,
        market_data_symbol:
          marketDataSymbol ||
          resolved.listing?.market_data_symbol ||
          resolved.listing?.resolved_symbol ||
          null,
      }

      // Do not abandon up-front: Back / option changes stay reversible until Start over.
      // If a prior Start over failed to clear the IP lock, abandon+retry once here.
      let prep
      try {
        prep = await postDemoPrep({
          cohortId: session.cohortId,
          listing,
          candleTimeframe,
          honeypot: "",
        })
      } catch (prepErr) {
        if (!isDemoEntryAnalysisInflight(prepErr)) throw prepErr
        const latest = readDemoSession()
        if (latest?.jobId && latest.handoffCode) {
          writeDemoSession({ step: "generating" })
          trackDemoEntryAnalysisEvent("prep_inflight_resumed", {
            job_id: latest.jobId,
            symbol: tradeSymbol,
          })
          history.push(`${DEMO_ENTRY_ANALYSIS_BASE_PATH}/generating`)
          return
        }
        trackDemoEntryAnalysisEvent("prep_inflight_abandon_retry", { symbol: tradeSymbol })
        await abandonDemoEntryAnalysis(latest?.jobId)
        writeDemoSession({
          jobId: undefined,
          handoffCode: undefined,
          pollToken: undefined,
          claimed: false,
          claimAttempted: false,
          readyPayload: null,
        })
        prep = await postDemoPrep({
          cohortId: session.cohortId,
          listing,
          candleTimeframe,
          honeypot: "",
        })
      }

      writeDemoSession({
        step: "generating",
        symbol: tradeSymbol,
        candleTimeframe,
        listing,
        jobId: prep.job_id,
        handoffCode: prep.handoff_code,
        pollToken: undefined,
        claimed: false,
        claimAttempted: false,
        readyPayload: null,
      })
      trackDemoEntryAnalysisEvent("prep_started", {
        job_id: prep.job_id,
        symbol: tradeSymbol,
        reused: Boolean(prep.reused),
      })
      history.push(`${DEMO_ENTRY_ANALYSIS_BASE_PATH}/generating`)
    } catch (err) {
      setError(demoErrorUserMessage(err))
      trackDemoEntryAnalysisEvent("prep_fail", { symbol: tradeSymbol })
    } finally {
      prepInFlightRef.current = false
      setBusy(false)
    }
  }, [
    busy,
    candleTimeframe,
    futuresRecentTailBlocked,
    history,
    mdSourceOptions,
    needsQuoteChoice,
    noneLocatable,
    quoteCandidates,
    quoteVariant,
    selectedMdSource,
    selectedRow,
    session?.cohortId,
    symbol,
  ])

  const onStartOver = useCallback(() => {
    void restartDemoEntryAnalysis((path) => history.push(path))
  }, [history])

  if (!session?.cohortId) {
    return (
      <DemoEntryAnalysisShell activeStepIndex={2}>
        <DemoLoadingBlock centered />
      </DemoEntryAnalysisShell>
    )
  }

  return (
    <DemoEntryAnalysisShell
      activeStepIndex={2}
      title="Instrument & timeframe"
      subtitle="Entry Analysis is single-symbol and uses roughly the last 30 days of closed trades from your uploaded file. Confirm the instrument Tessera should analyse (preselected: most trades)."
      footer={
        <DemoWizardFooter
          onCancel={onStartOver}
          cancelLabel="Start over"
          onBack={() => history.push(DEMO_ENTRY_ANALYSIS_BASE_PATH)}
          showBack
          onContinue={() => void onContinue()}
          continueLabel="Generate my report"
          continueDisabled={
            busy ||
            noneLocatable ||
            Boolean(futuresRecentTailBlocked) ||
            !symbol ||
            (needsQuoteChoice && !quoteVariant)
          }
          continueLoading={busy}
          continueLoadingLabel="Starting…"
        />
      }
    >
      <div className="flex w-full min-w-0 flex-col gap-5">
        {noneLocatable ? (
          <p className={cn(AUTH_FONT, AUTH_MUTED_TEXT_CLASS, "text-sm")}>
            {DEMO_NONE_LOCATABLE_COPY}
          </p>
        ) : (
          <div className="grid w-full min-w-0 grid-cols-1 gap-5 sm:grid-cols-2">
            <DemoWizardFieldBlock label="Instrument" description={instrumentDescription} required>
              <TradeFilterSelectFullWidth
                value={symbol}
                onValueChange={handleInstrumentChange}
                options={instrumentOptions}
                placeholder={
                  instrumentOptions.length === 0 ? "No instruments in scope" : "Choose instrument"
                }
                emptyValueShowsPlaceholder
              />
            </DemoWizardFieldBlock>

            {needsQuoteChoice ? (
              <DemoWizardFieldBlock
                label="Quote / perp variant"
                description="Perps use futures OHLC; USDT/USDC use spot. Tessera picks a central data source (CoinAPI, Twelve Data, or Hyperliquid)."
                required
              >
                <TradeFilterSelectFullWidth
                  value={quoteVariant}
                  onValueChange={handleQuoteVariantChange}
                  options={quoteOptions}
                  placeholder="Choose quote variant"
                  emptyValueShowsPlaceholder
                />
              </DemoWizardFieldBlock>
            ) : null}

            {needsMdChoice && mdSourceOptions.length > 1 ? (
              <DemoWizardFieldBlock
                label="Market data source"
                description="Choose which central OHLC provider Tessera should use for this symbol."
                required
              >
                <TradeFilterSelectFullWidth
                  value={mdSourceKey}
                  onValueChange={setMdSourceKey}
                  options={mdSourceOptions.map((src, index) => ({
                    value: demoMdSourceValue(src, index),
                    label: formatMarketDataSourceSelectLabel(src),
                  }))}
                  placeholder="Choose source"
                  emptyValueShowsPlaceholder
                />
              </DemoWizardFieldBlock>
            ) : null}

            <DemoWizardFieldBlock
              label="Timeframe"
              description={candleTimeframeDescription}
              required
            >
              <TradeFilterSelectFullWidth
                value={candleTimeframe}
                onValueChange={(v) =>
                  setCandleTimeframe(
                    normalizeDemoCandleTimeframe(v as DemoCandleTimeframe, databentoListing)
                  )
                }
                options={candleTimeframeOptions}
                placeholder="Choose timeframe"
                emptyValueShowsPlaceholder
              />
            </DemoWizardFieldBlock>
          </div>
        )}

        {futuresRecentTailBlocked ? (
          <DemoWizardRecoveryAlert
            title="Market data not ready yet"
            message={futuresRecentTailBlocked}
            showSyncAnother
            onSyncAnother={() => history.push(DEMO_ENTRY_ANALYSIS_BASE_PATH)}
            onStartOver={onStartOver}
          />
        ) : null}

        {error && error !== futuresRecentTailBlocked ? (
          <DemoWizardRecoveryAlert
            title="Couldn’t continue"
            message={error}
            showSyncAnother={
              error === DEMO_SYNC_OTHER_CSV_COPY ||
              error === DEMO_NONE_LOCATABLE_COPY ||
              error === DEMO_FUTURES_RECENT_TAIL_COPY
            }
            onSyncAnother={() => history.push(DEMO_ENTRY_ANALYSIS_BASE_PATH)}
            onStartOver={onStartOver}
          />
        ) : null}
      </div>
    </DemoEntryAnalysisShell>
  )
}
